import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteNav } from "@/components/site/SiteNav";
import { Footer } from "@/components/site/Sections";
import {
  Radio,
  CheckCircle2,
  ArrowRight,
  ChevronRight,
  Sparkles,
  BarChart3,
  TrendingUp,
  Target,
  Users,
  Award,
  Globe,
  Clock,
  ShieldCheck,
  Check,
  Building2,
  FileCheck,
  Layers,
  Search,
  Volume2,
  Mic,
  DollarSign,
  Repeat,
  Headphones,
  Calendar,
} from "lucide-react";

export const Route = createFileRoute("/services_/radio")({
  head: () => ({
    meta: [
      {
        title:
          "Radio Advertising Services — KMWAdvertising · FM Networks & Audio Ads",
      },
      {
        name: "description",
        content:
          "Radio Advertising Services That Amplify Brand Awareness and Drive Local Market Engagement. FM Radio Media Planning, Spot Buying & RJ Mentions by KMWA LLP.",
      },
      {
        property: "og:title",
        content: "Radio Advertising Services — KMWAdvertising",
      },
      {
        property: "og:description",
        content:
          "Reach Your Audience Wherever They Are. FM & digital audio media buying across national, regional and local stations.",
      },
    ],
  }),
  component: RadioServicePage,
});

export function RadioServicePage() {
  const radioAdvantages = [
    {
      title: "Wide Audience Reach",
      desc: "FM and digital radio stations attract millions of listeners across metropolitan cities, tier-two cities and regional markets. Radio enables brands to reach diverse audiences without geographic limitations.",
      icon: Globe,
    },
    {
      title: "Cost-Effective Media Channel",
      desc: "Compared with television advertising, radio campaigns often require lower production and media costs while still delivering strong audience reach and frequency.",
      icon: DollarSign,
    },
    {
      title: "High Frequency & Reinforcement",
      desc: "Advertising success often depends on repeated exposure. Radio campaigns allow brands to increase message frequency efficiently, helping improve brand recall over time.",
      icon: Repeat,
    },
    {
      title: "Local Market Penetration",
      desc: "Radio stations often have strong regional identities and loyal audiences. This enables advertisers to target specific cities or language markets with highly relevant messages.",
      icon: Target,
    },
    {
      title: "Trusted Personal Medium",
      desc: "Radio presenters build long-term relationships with listeners, making radio an effective platform for brand endorsements, sponsored segments and promotional partnerships.",
      icon: Mic,
    },
    {
      title: "Complements Digital Marketing",
      desc: "Radio works exceptionally well alongside search, social media, TV, OOH and digital campaigns, encouraging listeners to search online, visit websites or engage across platforms.",
      icon: Sparkles,
    },
  ];

  const audienceResearchMetrics = [
    "Demographics & age groups",
    "Occupation & income levels",
    "Daily commuting patterns",
    "Listening habits & time spent",
    "Preferred radio stations & RJs",
    "Language & dialect preferences",
    "Geographic location & city tiers",
    "Consumer purchasing interests",
  ];

  const stationSelectionFactors = [
    "Listener demographics & age profile",
    "Market share & audience rating",
    "Geographic coverage & signal strength",
    "Content format (Music, Talk, News)",
    "Language alignment",
    "Audience loyalty & tune-in duration",
    "Time-band performance",
    "Station reputation & brand affinity",
  ];

  const timeBands = [
    { name: "Morning Drive Time", desc: "Peak commuting hours targeting professionals and students." },
    { name: "Office Hours Commute", desc: "Mid-morning listening in workplaces and transit." },
    { name: "Midday Programming", desc: "Targeting homemakers, shoppers, and retail audiences." },
    { name: "Evening Drive Time", desc: "High engagement during evening return commutes." },
    { name: "Weekend Entertainment", desc: "Leisure, lifestyle, and weekend shopping broadcasts." },
    { name: "Special Event Broadcasts", desc: "Sponsorships during festivals and concerts." },
  ];

  const radioBuyingServices = [
    "Airtime slot booking",
    "Station rate negotiations",
    "Spot scheduling & daypart placement",
    "Sponsorship packages & RJ mentions",
    "Promotional contest integrations",
    "Seasonal & festival campaigns",
    "Multi-city radio buying networks",
    "National campaign execution",
    "Regional language station buying",
    "Log & release order verification",
  ];

  return (
    <div className="dark relative min-h-screen bg-background text-foreground flex flex-col w-full overflow-x-clip">
      <SiteNav />

      <main className="flex-1 w-full overflow-x-clip pt-24 md:pt-28">
        {/* HERO SECTION */}
        <section className="relative overflow-hidden py-14 sm:py-20 md:py-24 border-b border-white/10">
          <div className="absolute inset-0 grid-bg opacity-15" aria-hidden />
          <div className="glow-orb -left-32 top-10 h-[450px] w-[450px] bg-[var(--glow-orange)] opacity-25" />
          <div className="glow-orb right-[-100px] top-20 h-[400px] w-[400px] bg-[var(--glow-purple)] opacity-25" />

          <div className="container-x relative z-10">
            {/* Breadcrumb */}
            <div className="flex flex-wrap items-center gap-2.5 text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6">
              <Link to="/" className="hover:text-white transition">
                Home
              </Link>
              <ChevronRight className="h-3 w-3 text-muted-foreground" />
              <Link to="/services" className="hover:text-white transition">
                Services
              </Link>
              <ChevronRight className="h-3 w-3 text-muted-foreground" />
              <span className="text-[var(--glow-orange)] font-semibold">Radio Advertising</span>
            </div>

            <div className="max-w-4xl">
              <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-muted-foreground border border-white/10 mb-6">
                <Radio className="h-3.5 w-3.5 text-[var(--glow-orange)]" />
                Traditional Media Capabilities
              </div>

              <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.12] tracking-tight text-white">
                Radio Advertising Services That Amplify Brand Awareness and Drive Local Market Engagement
              </h1>

              <p className="mt-6 text-lg sm:text-xl font-accent italic text-[var(--glow-orange)] font-medium border-l-2 border-[var(--glow-orange)] pl-4 py-1">
                Reach Your Audience Wherever They Are
              </p>

              <div className="mt-6 space-y-4 text-base sm:text-lg text-white/90 font-normal leading-relaxed">
                <p>
                  In an increasingly connected world, radio continues to be one of the most trusted, accessible and cost-effective advertising mediums. Whether people are commuting to work, driving long distances, shopping, exercising or listening through mobile apps, radio remains a constant companion that delivers timely, engaging and memorable brand messages.
                </p>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  Unlike many digital channels where advertisements compete for screen space, radio communicates directly through voice, music and storytelling, creating an intimate connection with listeners. This unique ability to engage audiences without visual distractions makes radio advertising an effective medium for increasing brand awareness, supporting retail promotions, driving local footfall and reinforcing integrated marketing campaigns.
                </p>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  At <strong className="text-white">KMWAdvertising LLP</strong>, we provide comprehensive radio advertising services that combine strategic planning, audience insights, creative production and media buying to help businesses reach the right listeners at the right time. With more than two decades of experience, we work with leading national and regional radio networks to create campaigns that deliver measurable reach, frequency and brand impact.
                </p>
              </div>

              <div className="mt-8 flex flex-wrap gap-4">
                <a href="/contact-us" className="btn-hero !py-3.5 !px-7">
                  Request Radio Campaign Proposal
                  <ArrowRight className="h-4 w-4" />
                </a>
                <Link to="/services" className="btn-ghost !py-3.5 !px-7">
                  Explore All Services
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* WHY RADIO DELIVERS & ADVANTAGES */}
        <section className="py-14 sm:py-20 border-b border-white/10 bg-white/[0.01]">
          <div className="container-x">
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-orange)] block mb-2">
                High-Frequency Audio Engagement
              </span>
              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-white">
                Why Radio Advertising Still Delivers Results
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground mt-3 leading-relaxed">
                Despite the growth of digital marketing, radio continues to play a significant role in the media mix because of its accessibility, affordability and ability to reach audiences throughout the day. Radio offers brands an opportunity to engage consumers during moments when other media channels may not be available, particularly while driving, commuting or working.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {radioAdvantages.map((adv, idx) => {
                const AIcon = adv.icon;
                return (
                  <div
                    key={idx}
                    className="p-6 sm:p-8 rounded-2xl glass-strong border border-white/10 hover:border-white/20 transition duration-300 flex flex-col justify-between group"
                  >
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-orange)]">
                          ADVANTAGE 0{idx + 1}
                        </span>
                        <div className="h-10 w-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-white">
                          <AIcon className="h-5 w-5" />
                        </div>
                      </div>
                      <h3 className="font-display text-xl font-bold text-white mb-2">
                        {adv.title}
                      </h3>
                      <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                        {adv.desc}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* OUR RADIO ADVERTISING SERVICES */}
        <section className="py-16 md:py-24 border-b border-white/10 relative">
          <div className="container-x">
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-orange)] block mb-2">
                Strategic Audio Execution
              </span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">
                Our Radio Advertising Services
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground mt-2">
                KMWAdvertising LLP offers end-to-end radio advertising solutions, ensuring every campaign is strategically planned, professionally executed and continuously optimised.
              </p>
            </div>

            {/* RADIO MEDIA PLANNING */}
            <div className="mb-16">
              <div className="border-b border-white/10 pb-4 mb-8">
                <h3 className="font-display text-2xl font-bold text-white flex items-center gap-2.5">
                  <BarChart3 className="h-5 w-5 text-[var(--glow-orange)]" />
                  Radio Media Planning
                </h3>
                <p className="text-xs sm:text-sm font-mono text-muted-foreground uppercase tracking-wider mt-1">
                  Strategic Planning That Maximises Reach and Frequency
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-6 mb-12">
                {/* Audience Research */}
                <div className="p-6 rounded-2xl glass-strong border border-white/10 space-y-4">
                  <h4 className="font-display text-lg font-bold text-white flex items-center gap-2">
                    <Users className="h-4 w-4 text-[var(--glow-orange)]" />
                    Audience Research
                  </h4>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Understanding listener behaviour is fundamental to campaign success:
                  </p>
                  <div className="space-y-1.5">
                    {audienceResearchMetrics.map((item, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs text-white/90">
                        <Check className="h-3 w-3 text-emerald-400 shrink-0" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Station Selection */}
                <div className="p-6 rounded-2xl glass-strong border border-white/10 space-y-4">
                  <h4 className="font-display text-lg font-bold text-white flex items-center gap-2">
                    <Radio className="h-4 w-4 text-[var(--glow-orange)]" />
                    Radio Station Selection
                  </h4>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    We evaluate each broadcaster based on key performance metrics:
                  </p>
                  <div className="space-y-1.5">
                    {stationSelectionFactors.map((item, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs text-white/90">
                        <Check className="h-3 w-3 text-emerald-400 shrink-0" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Time Band Planning */}
                <div className="p-6 rounded-2xl glass-strong border border-white/10 space-y-4">
                  <h4 className="font-display text-lg font-bold text-white flex items-center gap-2">
                    <Clock className="h-4 w-4 text-[var(--glow-orange)]" />
                    Time Band Planning
                  </h4>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Scheduling during high-impact listening windows throughout the day:
                  </p>
                  <div className="space-y-2">
                    {timeBands.map((tb, i) => (
                      <div key={i} className="p-2 rounded-lg bg-white/[0.03] border border-white/5">
                        <span className="text-xs font-semibold text-white block">{tb.name}</span>
                        <span className="text-[11px] text-muted-foreground">{tb.desc}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* RADIO MEDIA BUYING */}
            <div className="p-8 sm:p-10 rounded-3xl glass-strong border border-white/15 relative overflow-hidden shadow-2xl">
              <div className="glow-orb -top-32 -right-32 h-96 w-96 rounded-full bg-[var(--glow-orange)] opacity-15" />

              <div className="grid lg:grid-cols-12 gap-8 items-center relative z-10">
                <div className="lg:col-span-5 space-y-4">
                  <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-orange)] block">
                    FM Airtime Negotiation
                  </span>
                  <h3 className="font-display text-2xl sm:text-3xl font-bold text-white">
                    Radio Media Buying
                  </h3>
                  <p className="text-sm font-accent italic text-white/90 border-l-2 border-[var(--glow-orange)] pl-3">
                    Secure Premium Airtime at Competitive Rates
                  </p>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    Media buying plays a critical role in campaign success. Our experienced radio buying specialists negotiate directly with leading FM networks, regional broadcasters and private radio stations to secure high-value advertising opportunities at competitive prices.
                  </p>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    By leveraging our industry relationships and buying expertise, we help clients maximise campaign performance while maintaining budget efficiency.
                  </p>
                </div>

                <div className="lg:col-span-7 bg-[#06060c]/90 rounded-2xl p-6 border border-white/10">
                  <h4 className="text-xs font-mono uppercase tracking-widest text-white/80 mb-4 block">
                    Our Radio Buying Services Include:
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    {radioBuyingServices.map((service, sIdx) => (
                      <div
                        key={sIdx}
                        className="flex items-center gap-2.5 p-3 rounded-xl bg-white/[0.03] border border-white/5 text-xs text-white/90"
                      >
                        <ShieldCheck className="h-4 w-4 text-[var(--glow-orange)] shrink-0" />
                        <span>{service}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CLOSING CTA BANNER */}
        <section className="py-20 md:py-28 relative overflow-hidden">
          <div className="glow-orb left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[500px] w-[500px] bg-[var(--glow-orange)] opacity-20" />

          <div className="container-x relative z-10 text-center max-w-4xl">
            <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 text-xs uppercase tracking-widest text-muted-foreground border border-white/10 mb-6">
              <Radio className="h-3.5 w-3.5 text-[var(--glow-orange)]" />
              Radio Media Advisory
            </div>

            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-white leading-tight">
              Ready to Amplify Your Brand with Radio Advertising?
            </h2>

            <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Connect with KMWAdvertising LLP's radio media buying and planning specialists to build high-impact audio campaigns tailored to your target audience.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="/contact-us" className="btn-hero w-full sm:w-auto">
                Request Radio Proposal
                <ArrowRight className="h-4 w-4" />
              </a>
              <Link to="/services" className="btn-ghost w-full sm:w-auto">
                Back to All Services
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
